import { CTF_ADDRESS, sameAddress } from '@/client/confs/app';
import { useMarketBaseToken } from '@/client/hooks/use-market-base-token';
import { useOnChainBalance, useOnChainPosition } from '@/client/hooks/use-on-chain';
import { useSingerTokenBalance } from '@/client/hooks/use-signed-token-balance';
import { useSignedUserAssets } from '@/client/hooks/use-signed-user-assets';
import { useSignedUserInfo } from '@/client/hooks/use-signed-user-info';
import { TradeType } from '@/client/store/trade-store';
import { api } from '@/client/utils/api';
import { useMarketDetail } from '@/libs/react-sdk/api/use-market-detail';
import { useUserPositions } from '@/libs/react-sdk/api/use-user-options';
import { useMemo } from 'react';
import { Address } from 'viem';

type BalanceProps = {
  type: TradeType;
  marketAddress: string;
  optionAddress?: string;
  uid: string;
};

export const useBalance = ({ type, uid, marketAddress, optionAddress }: BalanceProps) => {
  const userAssets = useSignedUserAssets(api);
  const market = useMarketDetail(api, { marketAddress }, { subscribe: false });
  // Balance must be denominated in the traded market's own base token, not
  // the global toggle token (mixed multi-base lists)
  const baseToken = useMarketBaseToken(market.data?.baseTokenAddress);
  // The header's balance query (one multicall for the signer's base tokens, 60 s)
  // is the panel's balance too: the refresh after a trade, withdraw, claim or
  // stake reaches the header, the panel and the confirm button at once.
  const tokenBalances = useSingerTokenBalance(api);
  const onChainBalance = tokenBalances.data?.find(item => sameAddress(item.baseTokenAddress, baseToken.address));
  const params = useMemo(() => {
    return {
      page: 1,
      marketAddress,
      optionAddress,
      uid,
    };
  }, [marketAddress, optionAddress, uid]);
  const userPositions = useUserPositions(api, params);
  const positions = useMemo(
    () => userPositions.data?.pages.flatMap(item => item.positions) || [],
    [userPositions.data?.pages]
  );
  const optionsPosition = useMemo(
    () => positions.find(item => item.optionAddress.toLowerCase() === optionAddress?.toLowerCase()),
    [optionAddress, positions]
  );

  // On-chain balances via wallet RPC provider
  const { data: userInfo } = useSignedUserInfo(api);
  const walletAddress = userInfo?.eoaAddress as Address | undefined;
  // Own read of the market's token only while the shared read has failed without
  // a result (it goes through the wallet's RPC when a wallet is connected).
  const needsOwnBalanceRead = tokenBalances.isError && tokenBalances.data === undefined;
  const walletBalance = useOnChainBalance(
    needsOwnBalanceRead ? (baseToken.address as Address | undefined) : undefined,
    walletAddress
  );
  const positionId = useMemo(() => {
    if (!optionAddress || !market.data?.optionList) return undefined;
    const option = market.data.optionList.find(item => item.optionAddress.toLowerCase() === optionAddress.toLowerCase());
    return option?.positionId ? BigInt(option.positionId) : undefined;
  }, [optionAddress, market.data?.optionList]);

  // 60 s poll; the user's SSE events and the trade refresh re-read it at once.
  const onChainPosition = useOnChainPosition(CTF_ADDRESS as Address | undefined, walletAddress, positionId, {
    marketStatus: market.data?.marketStatus,
    sse: api.sse,
  });

  // Compute balance without useMemo — React Query data changes trigger re-render anyway
  let data: bigint | undefined;
  if (type === 'buy') {
    if (onChainBalance?.balance != null) {
      data = onChainBalance.balance;
    } else if (walletBalance.data != null) {
      data = walletBalance.data;
    } else if (userAssets.data?.balance != null && userAssets.data.balance !== '0') {
      data = BigInt(userAssets.data.balance);
    }
  } else {
    // Prefer on-chain CTF balance, fall back to DB-based position balance
    if (onChainPosition.data !== undefined) {
      data = onChainPosition.data;
    } else if (optionsPosition?.balance !== undefined) {
      data = BigInt(optionsPosition.balance);
    }
  }
  const isLoading = useMemo(() => {
    if (type === 'buy') {
      // Loading if ALL balance sources in use are still loading/pending
      return (
        (tokenBalances.isLoading || (!tokenBalances.data && tokenBalances.fetchStatus !== 'idle')) &&
        (!needsOwnBalanceRead || walletBalance.isLoading) &&
        userAssets.isLoading
      );
    }
    // Sell: loading if both on-chain and DB position are still loading
    const onChainPositionLoading = onChainPosition.isLoading && onChainPosition.fetchStatus !== 'idle';
    return onChainPositionLoading && userPositions.isLoading;
  }, [
    type,
    tokenBalances.isLoading,
    tokenBalances.data,
    tokenBalances.fetchStatus,
    needsOwnBalanceRead,
    walletBalance.isLoading,
    userAssets.isLoading,
    onChainPosition.isLoading,
    onChainPosition.fetchStatus,
    userPositions.isLoading,
  ]);
  const buyDecimal = useMemo(
    () => onChainBalance?.baseTokenDecimals ?? userAssets.data?.decimal,
    [onChainBalance?.baseTokenDecimals, userAssets.data?.decimal]
  );
  const sellDecimal = useMemo(() => optionsPosition?.optionDecimal, [optionsPosition?.optionDecimal]);
  const decimal = useMemo(
    () => (type == 'buy' ? buyDecimal : sellDecimal) ?? baseToken.decimals,
    [buyDecimal, sellDecimal, type, baseToken.decimals]
  );

  return {
    data,
    decimal,
    buyDecimal,
    sellDecimal,
    isLoading,
  };
};
